import React, { useState, useRef, useCallback } from "react";
import { Link } from "react-router-dom";
import { ChevronLeft, ChevronRight } from "lucide-react";

/**
 * ProductCardGallery — thumbnail carousel for the product grid.
 *
 * Behaviour:
 *  - Default image is images[0] (the front cover).
 *  - Desktop hover: cross-fades to the next image (index 1) for a "peek inside"
 *    effect; returns to cover on mouse-leave.
 *  - Manual controls (chevrons) appear on hover — users can browse ALL images
 *    regardless of how many the product has.
 *  - Small dot indicators sit at the bottom when there are 2+ images.
 *  - Mobile: swipe left/right to move between images (touch handlers).
 *  - Fully adaptive: works with 1, 2, 3, or more images.
 */
export default function ProductCardGallery({ images = [], alt = "", slug = "", to, aspectClass = "aspect-[5/7]", bgClass = "bg-brand-surface", onNavigate }) {
  const list = Array.isArray(images) && images.length > 0 ? images : [""];
  const total = list.length;
  const [index, setIndex] = useState(0);
  const touchStartX = useRef(null);
  const hoverPrev = useRef(0);

  const goTo = useCallback((next) => {
    const wrapped = ((next % total) + total) % total;
    setIndex(wrapped);
  }, [total]);

  const onPrev = (e) => {
    e.preventDefault();
    e.stopPropagation();
    goTo(index - 1);
  };
  const onNext = (e) => {
    e.preventDefault();
    e.stopPropagation();
    goTo(index + 1);
  };

  // Desktop hover peek: transition to image 1 on enter, back to previous on leave.
  const onMouseEnter = () => {
    if (total < 2) return;
    hoverPrev.current = index;
    // Only auto-peek when we're currently on the cover — otherwise respect
    // the user's manual position.
    if (index === 0) setIndex(1);
  };
  const onMouseLeave = () => {
    if (total < 2) return;
    setIndex(0);
  };

  // Mobile swipe
  const onTouchStart = (e) => {
    touchStartX.current = e.touches[0].clientX;
  };
  const onTouchEnd = (e) => {
    if (touchStartX.current == null || total < 2) return;
    const delta = e.changedTouches[0].clientX - touchStartX.current;
    const threshold = 40;
    if (delta > threshold) goTo(index - 1);
    else if (delta < -threshold) goTo(index + 1);
    touchStartX.current = null;
  };

  const Wrapper = to ? Link : "div";
  const wrapperProps = to ? { to, onClick: onNavigate } : {};

  return (
    <Wrapper
      {...wrapperProps}
      data-testid={`product-gallery-${slug}`}
      className={`relative img-zoom ${aspectClass} ${bgClass} overflow-hidden block group/gallery select-none`}
      onMouseEnter={onMouseEnter}
      onMouseLeave={onMouseLeave}
      onTouchStart={onTouchStart}
      onTouchEnd={onTouchEnd}
    >
      {list.map((src, i) => (
        <img
          key={`${slug}-img-${i}`}
          src={src}
          alt={i === 0 ? alt : `${alt} — view ${i + 1}`}
          draggable={false}
          className="absolute inset-0 w-full h-full object-cover transition-opacity duration-500 ease-out"
          style={{ opacity: i === index ? 1 : 0 }}
        />
      ))}

      {total > 1 && (
        <>
          {/* Chevrons — desktop only, appear on hover */}
          <button
            type="button"
            aria-label="Previous image"
            data-testid={`gallery-prev-${slug}`}
            onClick={onPrev}
            className="hidden md:flex absolute top-1/2 left-2 -translate-y-1/2 w-8 h-8 items-center justify-center bg-white/85 hover:bg-white text-brand-dark opacity-0 group-hover/gallery:opacity-100 transition-opacity backdrop-blur-sm rounded-full shadow-sm"
          >
            <ChevronLeft strokeWidth={1.5} className="w-4 h-4" />
          </button>
          <button
            type="button"
            aria-label="Next image"
            data-testid={`gallery-next-${slug}`}
            onClick={onNext}
            className="hidden md:flex absolute top-1/2 right-2 -translate-y-1/2 w-8 h-8 items-center justify-center bg-white/85 hover:bg-white text-brand-dark opacity-0 group-hover/gallery:opacity-100 transition-opacity backdrop-blur-sm rounded-full shadow-sm"
          >
            <ChevronRight strokeWidth={1.5} className="w-4 h-4" />
          </button>

          {/* Dot indicators */}
          <div className="absolute bottom-3 left-1/2 -translate-x-1/2 flex items-center gap-1.5">
            {list.map((_, i) => (
              <button
                key={`${slug}-dot-${i}`}
                type="button"
                aria-label={`Go to image ${i + 1}`}
                onClick={(e) => { e.preventDefault(); e.stopPropagation(); goTo(i); }}
                className={`w-1.5 h-1.5 rounded-full transition-all ${i === index ? "bg-brand-dark w-4" : "bg-brand-dark/40 hover:bg-brand-dark/70"}`}
              />
            ))}
          </div>
        </>
      )}
    </Wrapper>
  );
}
